import { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DoneIcon from "@mui/icons-material/Done";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EmojiPicker from "emoji-picker-react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import {
  findBySala,
  save,
  attachFile,
  downloadFile,
  existsSalaMensagemLast,
  marcarComoNaoLida,
} from "./api-sala-mensagem";
import { ADMIN, COORDENADOR, PROFESSOR } from "../../Usuario/Perfil";
import { setQuantNaoLidas } from "../SalaParticipante/actions/actions";
import { SalaAvatar } from "../MensagemTable/SalaListItem";
import AttachFile from "./AttachFile";
import FormSendMessage from "./FormSendMessage";
import Mensagem from "./Mensagem";
import PictureAttach from "./PictureAttach";
import SMMenuOptions from "./SMMenuOptions";
import { createAndDownloadBlobFile } from "../../../util/DownloadUtil";
import { dateTimeFormat } from "../../../util/DateUtil";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { success } from "../../../util/ToastUtil";
import Container from "../../UI/Container";
import Loading from "../../UI/Loading";

const POLLING_INTERVAL = 40000;

const EMPTY_PAGE = { content: [], number: 0, totalPages: 0 };

const dayLabel = (date) => {
  const day = dayjs(date);
  if (day.isSame(dayjs(), "day")) return "Hoje";
  if (day.isSame(dayjs().subtract(1, "day"), "day")) return "Ontem";
  return day.format("DD/MM/YYYY");
};

// Mantém a ordem da API (mais recente primeiro) e ignora mensagens já carregadas.
const mergeMessages = (atuais, novas, posicao) => {
  const conhecidas = new Set(atuais.map((sm) => sm.codigo));
  const ineditas = novas.filter((sm) => !conhecidas.has(sm.codigo));
  if (ineditas.length === 0) return atuais;
  return posicao === "inicio" ? [...ineditas, ...atuais] : [...atuais, ...ineditas];
};

const MessageBubble = ({ sm, isMine, showAuthor, selected, onClick, handleDownloadAttach }) => (
  <Box
    onClick={onClick}
    sx={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start", mb: 0.75 }}
  >
    <Box
      sx={{
        position: "relative",
        maxWidth: { xs: "85%", sm: "70%" },
        px: 1.75,
        py: 1,
        borderRadius: 3,
        borderBottomRightRadius: isMine ? 4 : undefined,
        borderBottomLeftRadius: isMine ? undefined : 4,
        bgcolor: isMine ? "primary.main" : "background.paper",
        color: isMine ? "primary.contrastText" : "text.primary",
        boxShadow: 1,
        outline: selected ? 2 : 0,
        outlineColor: "warning.main",
        outlineOffset: 2,
      }}
    >
      {showAuthor && (
        <Typography variant="caption" fontWeight={600} color="secondary" component="div">
          {sm.usuario.nome}
        </Typography>
      )}
      <Stack spacing={1}>
        <Mensagem sm={sm} />
        <PictureAttach sm={sm} />
        <AttachFile sm={sm} handleDownloadAttach={handleDownloadAttach} />
      </Stack>
      <Typography
        variant="caption"
        component="div"
        textAlign="right"
        title={dateTimeFormat(sm.createdAt)}
        sx={{ opacity: 0.75, mt: 0.25, fontSize: 11 }}
      >
        {dayjs(sm.createdAt).format("HH:mm")}
      </Typography>
      {selected && (
        <CheckCircleIcon
          fontSize="small"
          color="warning"
          sx={{ position: "absolute", top: -8, [isMine ? "left" : "right"]: -8, bgcolor: "background.paper", borderRadius: "50%" }}
        />
      )}
    </Box>
  </Box>
);

function SalaMensagemContent({ initialState }) {
  const [salaParticipante, setSalaParticipante] = useState(
    initialState.salaParticipante || { sala: initialState.sala },
  );
  const sala = salaParticipante.sala;
  const nameRoom = initialState.nameRoom;
  const user = useSelector((state) => state.usuario);
  const isAll = useSelector((state) => state.isAllMensagemTable);
  const quantNaoLidas = useSelector((state) => state.quantNaoLidas);
  const dispatch = useDispatch();

  const [salaMensagens, setSalaMensagens] = useState(EMPTY_PAGE);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isAttachingFile, setIsAttachingFile] = useState(false);
  const [listSMSelect, setListSMSelect] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const hiddenFileInput = useRef(null);
  // Referência para o polling ler as mensagens atuais sem reiniciar o intervalo.
  const contentRef = useRef([]);
  contentRef.current = salaMensagens.content;

  const podeGerenciar = [ADMIN, COORDENADOR, PROFESSOR].includes(user?.perfil);
  const podeSelecionar = user?.perfil === ADMIN;

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    findBySala(sala.codigo, 0)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setSalaMensagens(data);
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [sala.codigo]);

  const fetchNewest = useCallback(() => {
    findBySala(sala.codigo, 0).then((data) => {
      if (!data || data.error) return;
      setSalaMensagens((prevState) => ({
        ...prevState,
        content: mergeMessages(prevState.content, data.content, "inicio"),
      }));
    });
  }, [sala.codigo]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden) return;
      const ultima = contentRef.current[0];
      if (!ultima) {
        fetchNewest();
        return;
      }
      existsSalaMensagemLast(sala.codigo, ultima.codigo).then((isUltima) => {
        if (!isUltima) fetchNewest();
      });
    }, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [sala.codigo, fetchNewest]);

  const handleMoreMessages = () => {
    setIsLoadingMore(true);
    findBySala(sala.codigo, salaMensagens.number + 1)
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(data.message);
          return;
        }
        setSalaMensagens((prevState) => ({
          ...data,
          content: mergeMessages(prevState.content, data.content, "fim"),
        }));
      })
      .finally(() => setIsLoadingMore(false));
  };

  const addMessage = (data) => {
    setSalaMensagens((prevState) => ({
      ...prevState,
      content: mergeMessages(prevState.content, [data], "inicio"),
    }));
  };

  const handleSend = (event) => {
    event.preventDefault();
    const texto = mensagem.trim();
    if (!texto || isSending) return;

    setIsSending(true);
    save({ sala, mensagem: texto })
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível enviar a mensagem.");
          return;
        }
        addMessage(data);
        setMensagem("");
        setSalaParticipante((prevState) =>
          typeof prevState.quantNaoLidasOtherUser === "number"
            ? { ...prevState, quantNaoLidasOtherUser: prevState.quantNaoLidasOtherUser + 1 }
            : prevState,
        );
      })
      .finally(() => setIsSending(false));
  };

  const handleChangeFile = (event) => {
    const file = event.target.files?.[0];
    // Limpa o input para permitir anexar o mesmo arquivo novamente.
    event.target.value = "";
    if (!file) return;

    const fileFormData = new FormData();
    fileFormData.append("file", file);
    setIsAttachingFile(true);
    attachFile(sala.codigo, fileFormData)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível anexar o arquivo.");
          return;
        }
        addMessage(data);
      })
      .finally(() => setIsAttachingFile(false));
  };

  const handleDownloadAttach = (sm) => {
    downloadFile(sm.codigo, sm.fileName, sm.fileType).then((data) => {
      if (!data || data.error) {
        errorMessage(data?.message || "Não foi possível baixar o arquivo.");
        return;
      }
      createAndDownloadBlobFile(data, sm.fileName);
    });
  };

  const handleSMSelect = (sm) => {
    if (!podeSelecionar) return;
    setListSMSelect((prevState) =>
      prevState.some((s) => s.codigo === sm.codigo)
        ? prevState.filter((s) => s.codigo !== sm.codigo)
        : [...prevState, sm],
    );
  };

  const handleMarcarComoNaoLida = () => {
    setAnchorEl(null);
    marcarComoNaoLida(sala.codigo).then((data) => {
      if (data?.error) {
        errorMessage(data.message);
        return;
      }
      dispatch(setQuantNaoLidas(quantNaoLidas + 1));
      success("Mensagem marcada como não lida.");
    });
  };

  const onEmojiClick = (emojiData) => {
    setMensagem((prevState) => prevState + emojiData.emoji);
  };

  const mostrarAutor = sala.isGroup || isAll;
  const hasMore = salaMensagens.number < salaMensagens.totalPages - 1;
  const ultimaMinha = salaMensagens.content[0]?.usuario.id === user.id;
  const mostrarVisualizacao =
    ultimaMinha && !sala.isGroup && typeof salaParticipante.quantNaoLidasOtherUser === "number";

  const renderMessages = () => {
    if (isLoading) {
      return (
        <Box m="auto">
          <Loading description="Carregando Mensagens..." />
        </Box>
      );
    }
    if (salaMensagens.content.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" textAlign="center" m="auto">
          Nenhuma mensagem ainda. Envie a primeira!
        </Typography>
      );
    }

    // A caixa usa column-reverse: o primeiro elemento (mais recente) fica embaixo
    // e o scroll já começa no fim da conversa.
    const content = salaMensagens.content;
    return (
      <>
        {mostrarVisualizacao && (
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" mb={0.5}>
            {salaParticipante.quantNaoLidasOtherUser === 0 ? (
              <DoneAllIcon sx={{ fontSize: 16 }} color="primary" />
            ) : (
              <DoneIcon sx={{ fontSize: 16 }} color="disabled" />
            )}
            <Typography variant="caption" color="text.secondary">
              {salaParticipante.quantNaoLidasOtherUser === 0 ? "Visualizado" : "Não visualizado"}
            </Typography>
          </Stack>
        )}
        {content.map((sm, i) => {
          const anterior = content[i + 1];
          const novoDia = !anterior || !dayjs(anterior.createdAt).isSame(sm.createdAt, "day");
          const isMine = sm.usuario.id === user.id;
          return (
            <Box key={sm.codigo}>
              {novoDia && (
                <Divider sx={{ my: 1.5 }}>
                  <Chip size="small" label={dayLabel(sm.createdAt)} />
                </Divider>
              )}
              <MessageBubble
                sm={sm}
                isMine={isMine}
                showAuthor={mostrarAutor && !isMine}
                selected={listSMSelect.some((s) => s.codigo === sm.codigo)}
                onClick={() => handleSMSelect(sm)}
                handleDownloadAttach={handleDownloadAttach}
              />
            </Box>
          );
        })}
        {hasMore && (
          <Box textAlign="center" mb={1}>
            <Button size="small" onClick={handleMoreMessages} disabled={isLoadingMore}>
              {isLoadingMore ? "Carregando..." : "Carregar mensagens anteriores"}
            </Button>
          </Box>
        )}
      </>
    );
  };

  return (
    <Container>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
        <Tooltip title="Voltar para mensagens">
          <IconButton component={Link} to="/mensagem" edge="start">
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <SalaAvatar
          sala={sala}
          usuarios={
            salaParticipante.usuario
              ? [salaParticipante.usuario]
              : (sala.listaSP ?? []).map((sp) => sp.usuario)
          }
        />
        <Box flexGrow={1} minWidth={0}>
          <Typography variant="h6" noWrap>
            {nameRoom}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {sala.isGroup ? "Grupo" : salaParticipante.usuario?.perfil ?? "Conversa"}
          </Typography>
        </Box>
        {podeGerenciar && (
          <>
            <Tooltip title="Mais opções">
              <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem component={Link} to="/group-list-participants" state={{ sala }}>
                Lista Participantes
              </MenuItem>
              {salaParticipante.codigo && (
                <MenuItem onClick={handleMarcarComoNaoLida}>Marcar como não lida</MenuItem>
              )}
            </Menu>
          </>
        )}
      </Stack>

      <SMMenuOptions
        listSMSelect={listSMSelect}
        setListSMSelect={setListSMSelect}
        setSalaMensagens={setSalaMensagens}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column-reverse",
          height: "60vh",
          minHeight: 360,
          overflowY: "auto",
          p: { xs: 1.5, sm: 2 },
          borderRadius: 2,
          bgcolor: "action.hover",
        }}
      >
        {renderMessages()}
      </Box>

      <Popover
        open={Boolean(emojiAnchorEl)}
        anchorEl={emojiAnchorEl}
        onClose={() => setEmojiAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <EmojiPicker onEmojiClick={onEmojiClick} lazyLoadEmojis width={320} height={400} />
      </Popover>

      <FormSendMessage
        handleSend={handleSend}
        handleEmojiShow={(event) => setEmojiAnchorEl(event.currentTarget)}
        mensagem={mensagem}
        handleChange={(event) => setMensagem(event.target.value)}
        hiddenFileInput={hiddenFileInput}
        handleChangeFile={handleChangeFile}
        isAttachingFile={isAttachingFile}
        handleAttachFile={() => hiddenFileInput.current.click()}
        isSending={isSending}
      />
    </Container>
  );
}

export default function SalaMensagem() {
  const { state } = useLocation();

  // Ao abrir a URL direto (ou recarregar), não há sala no estado da navegação.
  if (!state?.salaParticipante && !state?.sala) {
    return (
      <Container>
        <Stack alignItems="center" spacing={2} py={6}>
          <Typography color="text.secondary">Nenhuma conversa selecionada.</Typography>
          <Button variant="contained" component={Link} to="/mensagem">
            Ir para Mensagens
          </Button>
        </Stack>
      </Container>
    );
  }

  return <SalaMensagemContent initialState={state} />;
}
